import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { Outlet } from "react-router-dom";
import { type AxiosError } from "axios";

import messageLevels from "@/constants/message-levels";
import useAuthHeader from "@/hooks/useAuthHeader";
import useToastAlert from "@/hooks/useToastAlert";

import {
	type Collection,
	createCollectionApi,
	deleteCollectionsApi,
	fetchCollections,
	patchCollectionApi,
} from "./collectionsApi";

export type { Collection };

export interface CreateCollectionInput {
	name: string;
	platformId: number;
	dataTypeId: number;
}

interface CollectionsContextValue {
	collections: Collection[];
	loading: boolean;
	refetchCollections: () => Promise<void>;
	getCollectionById: (id: string | number) => Collection | undefined;
	isCollectionNameTaken: (name: string, excludeId?: string | number) => boolean;
	createCollection: (input: CreateCollectionInput) => Promise<Collection>;
	renameCollection: (id: string | number, name: string) => Promise<void>;
	deleteCollections: (ids: (string | number)[]) => Promise<void>;
}

const CollectionsContext = createContext<CollectionsContextValue | null>(null);

function collectionIdMatches(a: string | number, b: string | number): boolean {
	return String(a) === String(b);
}

export function CollectionsProvider({ children }: { children?: ReactNode }) {
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const [collections, setCollections] = useState<Collection[]>([]);
	const [loading, setLoading] = useState(true);

	const refetchCollections = useCallback(async () => {
		setLoading(true);
		try {
			const data = await fetchCollections(authHeader);
			setCollections(data);
		} catch (err) {
			console.error(err);
			handleToastAlert(messageLevels.ERROR, "Could not load collections.");
		} finally {
			setLoading(false);
		}
	}, [authHeader, handleToastAlert]);

	useEffect(() => {
		void refetchCollections();
	}, [refetchCollections]);

	const normalizeCollectionName = useCallback((name: string): string => {
		return name.trim().toLowerCase();
	}, []);

	const isCollectionNameTaken = useCallback(
		(name: string, excludeId?: string | number) => {
			const normalized = normalizeCollectionName(name);
			if (!normalized) return false;
			return collections.some(
				(c) =>
					!(excludeId !== undefined && collectionIdMatches(c.id, excludeId)) &&
					normalizeCollectionName(c.name) === normalized,
			);
		},
		[collections],
	);

	const getCollectionById = useCallback(
		(id: string | number) => collections.find((c) => collectionIdMatches(c.id, id)),
		[collections],
	);

	const createCollection = useCallback(
		async (input: CreateCollectionInput): Promise<Collection> => {
			try {
				const created = await createCollectionApi(
					{
						name: input.name.trim(),
						platformId: input.platformId,
						dataTypeId: input.dataTypeId,
					},
					authHeader,
				);
				setCollections((prev) => [...prev, created]);
				return created;
			} catch (err) {
				const status = (err as AxiosError)?.response?.status;
				if (status === 409) {
					handleToastAlert(messageLevels.ERROR, "A collection with this name already exists.");
				} else {
					console.error(err);
					handleToastAlert(messageLevels.ERROR, "Could not create collection.");
				}
				throw err;
			}
		},
		[authHeader, handleToastAlert],
	);

	const renameCollection = useCallback(
		async (id: string | number, name: string) => {
			const trimmedName = name.trim();
			try {
				const updated = await patchCollectionApi(id, trimmedName, authHeader);
				setCollections((prev) =>
					prev.map((c) => (collectionIdMatches(c.id, id) ? updated : c)),
				);
			} catch (err) {
				const status = (err as AxiosError)?.response?.status;
				if (status === 409) {
					handleToastAlert(messageLevels.ERROR, "A collection with this name already exists.");
				} else {
					console.error(err);
					handleToastAlert(messageLevels.ERROR, "Could not rename collection.");
				}
				throw err;
			}
		},
		[authHeader, handleToastAlert],
	);

	const deleteCollections = useCallback(
		async (ids: (string | number)[]) => {
			const collectionIds = ids.map((id) => Number(id));
			try {
				await deleteCollectionsApi(collectionIds, authHeader);
				const idSet = new Set(ids.map(String));
				setCollections((prev) => prev.filter((c) => !idSet.has(String(c.id))));
			} catch (err) {
				console.error(err);
				handleToastAlert(messageLevels.ERROR, "Could not delete collection(s).");
				throw err;
			}
		},
		[authHeader, handleToastAlert],
	);

	const value = useMemo(
		(): CollectionsContextValue => ({
			collections,
			loading,
			refetchCollections,
			getCollectionById,
			isCollectionNameTaken,
			createCollection,
			renameCollection,
			deleteCollections,
		}),
		[
			collections,
			loading,
			refetchCollections,
			getCollectionById,
			isCollectionNameTaken,
			createCollection,
			renameCollection,
			deleteCollections,
		],
	);

	return (
		<CollectionsContext.Provider value={value}>
			{children ?? <Outlet />}
		</CollectionsContext.Provider>
	);
}

export function useCollections(): CollectionsContextValue {
	const ctx = useContext(CollectionsContext);
	if (!ctx) {
		throw new Error("useCollections must be used within CollectionsProvider");
	}
	return ctx;
}
